import useActionGuard from 'hooks/useActionGuard'
import { Link2, Send } from 'lucide-react'
import { staticState } from '@wellesley/host-remote'
import { FC, useState } from 'react'
import { getPostRoute } from 'helpers/routes'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { getDisplayUsername } from '@wellesley/user'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import { TooltipWrapper, useIsMobile } from '@wellesley/ui'
import {
  CreatePost as CreatePostProps,
  useDeleteApiV1PostsByPostIdRepostMutation,
} from '@wellesley/openapi'

import isQuote from '../helpers/isQuote'
import isRepost from '../helpers/isRepost'
import { PostForm } from '../../PostForm'
import BookmarkButton from '../../../bookmarks/BookmarkButton'
import useMakeComment from '../../../timeline/FeedPage/hooks/useMakeComment'
import {
  usePostApiV1PostsByPostIdRepostMutation,
  useDeleteApiV1PostsByPostIdMutation,
} from '../../api'

import Repost from './Repost'
import LikeButton from './LikeButton'
import CommentControl from './CommentControl'
import { CardAction } from './CardAction'
import { QuoteModal } from './QuoteModal'
import { FooterProps } from './types'
import { normalizeAtPathForBrowser } from '@wellesley/router'

const Footer: FC<FooterProps> = (props) => {
  const {
    feed,
    isFeedAuthor,
    openedReplyInputId,
    isDisabledCommentOption = true,
    renderFeed,
    onHighlight,
    onOpenReplyInput,
    renderUserWrapper,
    userMentionSearchScope,
    privacyOptionsVariant,
  } = props
  const [isQuoteModal, setIsQuoteOpen] = useState(false)
  const baseUrlParam = staticState.get().baseUrl

  const [copyPostLink] = useCopyToClipboard({
    textToCopy: `${baseUrlParam}${normalizeAtPathForBrowser(getPostRoute(feed.author, feed.id))}`,
    toastMsg: 'Link successfully copied',
  })

  const { isDisabled, guardedAction, ActionTooltipWrapper } = useActionGuard()
  const isPublicPost =
    feed.privacy === 'PUBLIC' || feed.effectivePrivacy === 'PUBLIC'

  const canRepostPost = !isDisabled && isPublicPost

  const [repost] = usePostApiV1PostsByPostIdRepostMutation()
  const [deletePost] = useDeleteApiV1PostsByPostIdMutation()
  const [deleteRepost] = useDeleteApiV1PostsByPostIdRepostMutation()
  const makeComment = useMakeComment({
    postId: feed.id,
  })

  const sharesCount =
    (Number(feed.stats.reposts) || 0) + (Number(feed.stats.quotes) || 0)

  const handleRepost = guardedAction(() => {
    const p = feed.originalPost?.privacy || feed.privacy
    const privacy = p === 'FOLLOW_POST' ? feed.effectivePrivacy || p : p

    void repost({
      postId: isQuote(feed) ? feed.id : feed.repostOf || feed.id,
      createPost: {
        text: '',
        privacy,
        kind: 'Note',
        attachments: [],
      },
    })
  })

  const isOpenReplyInput = openedReplyInputId === feed.id
  const handleOpenReplyInput = () => {
    onOpenReplyInput?.(feed.id)
  }

  const handleComment = async (data: CreatePostProps) => {
    await makeComment(data)
    handleOpenReplyInput()
  }

  const handleQuote = guardedAction(() => setIsQuoteOpen(true))
  const handleShare = () => void copyPostLink()

  const isRepostPost = isRepost(feed)

  const isHighlightedRepost = !!feed.reposted

  const canUndoRepost = !!feed.reposted
  const canDeleteRepost = (isRepostPost && !!isFeedAuthor) || !!feed.deleted

  const guardedRepost = guardedAction(
    canDeleteRepost || !!feed.deleted
      ? () => deletePost({ postId: props.postId || feed.id })
      : canUndoRepost
        ? () => deleteRepost({ postId: feed.id })
        : handleRepost,
  )

  const isMobile = useIsMobile()

  return (
    <>
      <div className="flex h-10 max-md:justify-between md:h-5 md:gap-8">
        {isQuoteModal && (
          <QuoteModal
            feed={feed}
            isOpenModal={isQuoteModal}
            renderFeed={renderFeed}
            renderUserWrapper={renderUserWrapper}
            onCloseModal={() => setIsQuoteOpen(false)}
          />
        )}

        <CommentControl
          {...props}
          isOpenComment={isOpenReplyInput}
          onSubmit={handleComment}
          onOpenChange={handleOpenReplyInput}
        />

        <Repost
          feed={feed}
          counter={sharesCount}
          onQuote={handleQuote}
          onRepost={guardedRepost}
          canRepost={canRepostPost}
          isFeedAuthor={isFeedAuthor}
          canUndoRepost={canUndoRepost || canDeleteRepost}
          isHighlighted={isHighlightedRepost}
        />

        <LikeButton feed={feed} isDisabled={isDisabled} />

        <ActionTooltipWrapper>
          <CardAction
            name="share"
            handler={handleShare}
            disabled={isDisabled}
            className="md:ml-auto"
          >
            <TooltipWrapper
              theme="dark"
              size="small"
              tooltipText={isDisabled ? '' : 'Share'}
            >
              <Link2 size={20} color="var(--gray-600)" />
            </TooltipWrapper>
          </CardAction>
        </ActionTooltipWrapper>
        <BookmarkButton
          postId={props.postId || feed.id}
          isBookmarked={!!feed.bookmarked}
          onHighlight={onHighlight}
        />
      </div>
      {!isMobile && isOpenReplyInput && (
        <div className="relative">
          <PostForm
            isReply
            userName={getDisplayUsername(getUserNameWithDomain(feed?.author))}
            onSubmit={handleComment}
            submitButtonText={<Send size={18} color="white" />}
            isDisabledCommentOption={isDisabledCommentOption}
            userMentionSearchScope={userMentionSearchScope}
            privacyOptionsVariant={privacyOptionsVariant}
          />
        </div>
      )}
    </>
  )
}

export { Footer }
